import {
    QueryClient,
    useQuery,
    useQueryClient,
    useMutation,
} from '@tanstack/react-query';

import { resetResultSimulationDataQueries } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Result/helpers/hooks/resultSimulationData';
import { SIMULATION_ENDPOINT_PREFIX } from 'routes/HumanResourcesModule/Simulation/helpers/constants';

import {
    RQAPICallWithData,
    HttpResponse,
    HttpContentWithData,
} from 'store/APIClient';
import { dispatch } from 'store/store';
import { useHypothesesChangesContext } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/components/contexts/HypothesesChangesContext';
import { RecurrenceEnum } from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/constants';
import { TRACKABLE_EVENTS, trackEventPRH } from 'helpers/trackerHelpers';
import {
    GradeAdvancementHypothesis,
    DetailedGradeAdvancementHypothesis,
} from '../types';

export type GradeAdvancementHypothesisMutationParams = {
    grade: string;
    startDate: string;
    salary: number;
    echelon: string;
};

const GRADE_ADVANCEMENT_HYPOTHESES_API_KEY = 'grade_advancement_hypotheses';
const DETAILED_GRADE_ADVANCEMENT_HYPOTHESES_API_KEY =
    'detailed_grade_advancement_hypotheses';

const invalidateDetailedGradeAdvancementHypothesesQueries = (
    queryClient: QueryClient,
    hrSimulationId: number,
) => {
    queryClient.invalidateQueries({
        queryKey: [
            DETAILED_GRADE_ADVANCEMENT_HYPOTHESES_API_KEY,
            hrSimulationId,
        ],
    });
};

export const useGetGradeAdvancementHypotheses = (simulationId: number) =>
    useQuery({
        queryKey: [GRADE_ADVANCEMENT_HYPOTHESES_API_KEY, simulationId],

        queryFn: async () => {
            const response = await RQAPICallWithData<
                GradeAdvancementHypothesis[]
            >({
                endpoint: `${SIMULATION_ENDPOINT_PREFIX}/${simulationId}/grade_advancement_hypotheses`,
            });
            return response.content.data;
        },
    });
export const useGetDetailedGradeAdvancementHypotheses = (
    hrSimulationId: number,
) =>
    useQuery({
        queryKey: [
            DETAILED_GRADE_ADVANCEMENT_HYPOTHESES_API_KEY,
            hrSimulationId,
        ],

        queryFn: async () => {
            const response = await RQAPICallWithData<
                DetailedGradeAdvancementHypothesis[]
            >({
                endpoint: `${SIMULATION_ENDPOINT_PREFIX}/${hrSimulationId}/detailed_grade_advancement_hypotheses`,
            });
            return response.content.data;
        },
    });

const useCreateGradeAdvancementHypothesisMutation = ({
    onSuccess,
    simulationId,
    recurrence,
    jobNumber,
    agentSoftwareId,
    mantyStatus,
    orgChartHrSoftwareId,
}: {
    onSuccess: () => void;
    simulationId: number;
    recurrence: RecurrenceEnum;
    jobNumber?: string | null;
    agentSoftwareId?: string;
    mantyStatus?: string;
    orgChartHrSoftwareId?: string;
}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            grade,
            echelon,
            salary,
            startDate,
        }: GradeAdvancementHypothesisMutationParams) => {
            if (
                !recurrence ||
                jobNumber === undefined ||
                !agentSoftwareId ||
                !mantyStatus ||
                !orgChartHrSoftwareId
            ) {
                return {} as Promise<
                    HttpResponse<
                        HttpContentWithData<GradeAdvancementHypothesis>
                    >
                >;
            }
            return RQAPICallWithData<GradeAdvancementHypothesis>({
                endpoint: `${SIMULATION_ENDPOINT_PREFIX}/${simulationId}/grade_advancement_hypotheses`,
                method: 'POST',
                body: {
                    mantyStatus,
                    agentSoftwareId,
                    jobNumber,
                    recurrence,
                    orgChartHrSoftwareId,
                    grade,
                    echelon,
                    salary,
                    startDate,
                },
            });
        },

        onSuccess: ({ content }) => {
            onSuccess();
            queryClient.setQueryData(
                [GRADE_ADVANCEMENT_HYPOTHESES_API_KEY, simulationId],
                (oldData: GradeAdvancementHypothesis[]) => [
                    ...oldData,
                    content.data,
                ],
            );
            dispatch.vision.notifyUser({
                message: content.message,
                snackType: 'success',
            });
            trackEventPRH(
                TRACKABLE_EVENTS.human_resources.simulation.recurrent
                    .createGradeAdvancementHypothesis,
            );
        },
        meta: { useGlobalOnError: true },
    });
};

const useUpdateGradeAdvancementHypothesisMutation = ({
    onSuccess,
    simulationId,
    hypothesisId,
}: {
    onSuccess: () => void;
    simulationId: number;
    hypothesisId?: number;
}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            grade,
            echelon,
            salary,
            startDate,
        }: GradeAdvancementHypothesisMutationParams) => {
            if (hypothesisId === undefined) {
                return { content: { message: '', data: undefined } };
            }

            return RQAPICallWithData<GradeAdvancementHypothesis>({
                endpoint: `${SIMULATION_ENDPOINT_PREFIX}/grade_advancement_hypothesis/${hypothesisId}`,
                method: 'PUT',
                body: {
                    grade,
                    echelon,
                    salary,
                    startDate,
                },
            });
        },

        onSuccess: ({ content }) => {
            onSuccess();
            dispatch.vision.notifyUser({
                message: content.message ?? '',
                snackType: 'success',
            });

            const responseData = content.data;
            queryClient.setQueryData(
                [GRADE_ADVANCEMENT_HYPOTHESES_API_KEY, simulationId],
                (oldData: GradeAdvancementHypothesis[]) =>
                    oldData.map((hypothesis) =>
                        hypothesis.id === hypothesisId && responseData
                            ? responseData
                            : hypothesis,
                    ),
            );
            dispatch.vision.notifyUser({
                message: content.message ?? '',
                snackType: 'success',
            });
        },
        meta: { useGlobalOnError: true },
    });
};

const useDeleteGradeAdvancementHypothesisMutation = ({
    onSuccess,
    simulationId,
    hypothesisId,
}: {
    onSuccess: () => void;
    simulationId: number;
    hypothesisId?: number;
}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            if (hypothesisId === undefined) return undefined;

            return RQAPICallWithData({
                endpoint: `${SIMULATION_ENDPOINT_PREFIX}/grade_advancement_hypothesis/${hypothesisId}`,
                method: 'DELETE',
            });
        },

        onSuccess: (response) => {
            onSuccess();
            const existingData = queryClient.getQueryData<
                GradeAdvancementHypothesis[]
            >([GRADE_ADVANCEMENT_HYPOTHESES_API_KEY, simulationId]);

            if (existingData && hypothesisId !== undefined) {
                const updatedData = existingData.filter(
                    (hypothesis) => hypothesis.id !== hypothesisId,
                );
                queryClient.setQueryData(
                    [GRADE_ADVANCEMENT_HYPOTHESES_API_KEY, simulationId],
                    updatedData,
                );
            }

            dispatch.vision.notifyUser({
                message: response?.content.message ?? '',
                snackType: 'success',
            });
        },
        meta: { useGlobalOnError: true },
    });
};

export const useGradeAdvancementHypothesisCRUDMutations = ({
    simulationId,
    recurrence,
    jobNumber,
    agentSoftwareId,
    mantyStatus,
    hypothesisId,
    orgChartHrSoftwareId,
}: {
    simulationId: number;
    recurrence: RecurrenceEnum;
    jobNumber?: string | null;
    agentSoftwareId?: string;
    mantyStatus?: string;
    orgChartHrSoftwareId?: string;
    hypothesisId?: number;
}) => {
    const { setHasChanges } = useHypothesesChangesContext();
    const queryClient = useQueryClient();
    const onSuccess = () => {
        invalidateDetailedGradeAdvancementHypothesesQueries(
            queryClient,
            simulationId,
        );
        resetResultSimulationDataQueries(queryClient, simulationId);
        setHasChanges(true);
    };

    const { mutate: createHypothesis, isPending: createLoading } =
        useCreateGradeAdvancementHypothesisMutation({
            onSuccess,
            simulationId,
            recurrence,
            jobNumber,
            agentSoftwareId,
            mantyStatus,
            orgChartHrSoftwareId,
        });
    const { mutate: editHypothesis, isPending: editLoading } =
        useUpdateGradeAdvancementHypothesisMutation({
            onSuccess,
            simulationId,
            hypothesisId,
        });
    const { mutate: deleteHypothesis, isPending: deleteLoading } =
        useDeleteGradeAdvancementHypothesisMutation({
            onSuccess,
            simulationId,
            hypothesisId,
        });

    return {
        createHypothesis,
        editHypothesis,
        deleteHypothesis,
        isLoading: createLoading || editLoading || deleteLoading,
    };
};
